// libraries
import type { IDealSummary as DealSummary, FeatureToggles, IIncentives as Incentives } from '@makemydeal/dr-platform-shared';
import type {
    BALLOON,
    CoBuyer,
    CreditDecisionDetails,
    CreditScore,
    CreditTiers,
    DealerSource,
    CreditScoreSource,
    FINANCE,
    IPenciledInputs,
    IPencilOfferInputs,
    IShopper,
    LEASE,
    ManualIncentiveEntry,
    ManualIncentives,
    OFFER_ENCRYPTION_FIELDS,
    OfferIncentive,
    OfferIncentives,
    OfferSettings,
    OfferType,
    PaymentGridRequestOptions,
    PaymentTerm,
    PriorLeaseBalance as PlatformPriorLeaseBalance,
    PriorLeaseBalanceApplyMethod as PlatformPriorLeaseBalanceApplyMethod,
    paymentServicesTypes,
    PenciledInputOfferType,
    ResidualValueType,
    SellRateItem,
    Terms,
    Vehicle
} from '@makemydeal/dr-platform-types';
import type { ExtensionType, IPaymentConfig, ITerm } from './paymentTypes';

export const MIN_ID_PARAMS = 2;
export const KEYID_DELIMETER = '_';

export type EncryptionFields = {
    [K in OFFER_ENCRYPTION_FIELDS]?: string | string[];
};

export type CreditApp = {
    creditAppReferenceId: string;
    accelerateCreditAppId?: string;
    coApplicantEmail?: string;
    decisionDetails?: Partial<CreditDecisionDetails>;
    shouldApplyDecisionDetails?: boolean;
};

export type CreditTierScoreHighLow = {
    high: number;
    low: number;
};

export type CreditTierNameAndScore = {
    name: string;
    score: CreditTierScoreHighLow;
    isDefault?: boolean;
};

export type CreditTierScoreOnly = {
    score: CreditTierScoreHighLow;
};

export type Dealer = {
    dealerId: number;
    ownerId: number;
    name: string;
    email: string;
    vppEnabled: boolean;
    address: DealerAddress;
    commonOrgId?: string;
};

export type DealerAddress = {
    address: string;
    zip: string;
};

export interface DealXgDetails {
    // Backend-provided fields
    globalCustomerId?: string;
    redirectionLink?: string;
    omsOrderNumber?: string;
    latestPushToDmsPlus?: string;
    t1OriginDealKey?: string;
    t1OriginDealSource?: string;
    t1OriginDealOrigin?: string;
    t1OriginDealLanguage?: string;
    t1OriginDealDeviceType?: string;
    t1OriginDealProvider?: string;
    autoSelectProgramSavedAt?: string;
    // Internal UI/control flags (added during INIT/reducer updates)
    honorOverrides?: boolean;
    // Map keyed by offer/deal type indicating if credit rating & terms are locked
    lockCreditRatingAndTermsByDealType?: Partial<Record<OfferType, boolean>>;
}

export type FinancingTerms = {
    term: number;
    apr: number;
};

export type LeasingTerms = {
    term: number;
    mileage: number;
};

export type IncentivesByOfferType = {
    finance: Incentives;
    lease: Incentives;
    cash: Incentives;
};

export type LenderProgramData = {
    lenderId: number;
    branchId?: number;
    regionalId: number;
    programType: string;
    programName: string;
    productType?: string;
    programStartDate?: string;
    programEndDate?: string;
    regionalTitle?: string;
    regionalDescription?: string;
    rateProgram: string;
    residualProgram?: string;
};

export type OfferSummary = {
    adjustedResidualValue: number;
    amountFinanced: number;
    annualMiles: number;
    dealSummary: DealSummary;
    downPayment: number;
    dueAtSigning: number;
    isFinanceNegativeTradeIn: boolean;
    listedPrice: number;
    monthlyPayment: number;
    paymentWithoutProducts?: number;
    monthlyPaymentRounded: number;
    offerPrice: number;
    initialOfferPrice?: number;
    offerType: string;
    residualPercentage: number;
    retailPrice: number;
    sellRate: number;
    term: {
        months: number;
        apr: number;
    };
    totalIncentives: number;
    tradeInAmountOwed: number;
    tradeInValue: number;
    vehicleType: string;
};

export type SpOffer = {
    connectionId: string;
    createdOn: Date;
    creditApp: CreditApp;
    creditScore: CreditScore;
    dealerId: number;
    downPayment: number;
    id: string;
    incentives: any; // TODO: Define type
    financeTerms: FinancingTerms;
    lead: any; // TODO: Define type
    monthlyPayment: number;
    offerPrice: number;
    initialOfferPrice?: number;
    shopper: IShopper;
    testDrive: TestDrive;
    totalFinanced: number;
    type: string;
};

export type OfferIncentiveWithAmount = OfferIncentive & {
    amount: number;
};

export type UserRowSource = ManualIncentiveEntry & { _editId?: string };

/**
 * A rebate row carries one of three provenances:
 *
 * | row kind                     | `manual` | `frozenFromProgram` | replaced by a good PQ |
 * | ---------------------------- | -------- | ------------------- | --------------------- |
 * | user-authored or user-edited | `true`   | absent              | no                    |
 * | program, frozen at UPQ       | `false`  | `true`              | yes                   |
 * | program, live on PQ          | `false`  | absent              | n/a - term-derived    |
 *
 * Reusing `manual: true` for a frozen row would make it permanent, and a later program selection could
 * never replace it. Editing a frozen row promotes it to a user row; removing one is its absence from the
 * set. An intersection rather than a field on the platform type, matching `UserRowSource` above.
 */
export type FrozenAwareIncentiveEntry = ManualIncentiveEntry & { frozenFromProgram?: boolean };

/**
 * Marks a bag the ledger has written, so one holding no frozen rows can be told apart from one that was
 * never populated - otherwise both read as "not hydrated yet" and the read path falls back to the term,
 * resurrecting a rebate the operator just removed.
 *
 * Set by the freeze and by load-time hydration, and must be preserved by every path that rewrites the bag.
 */
export type LedgerAwareManualIncentives = ManualIncentives & { ledgerOwned?: boolean };

export type RowProvenance = Partial<Pick<FrozenAwareIncentiveEntry, 'manual' | 'isStandardIncentive' | 'frozenFromProgram'>>;

export type TermIncentive = {
    id: number;
    amount?: number;
    apr?: number;
    name?: string;
    programId?: string;
    incentiveType?: string;
    isStandardIncentive?: boolean;
    exclusions?: number[];
    description?: string;
};

export type OfferIncentivesById = {
    [id: string]: OfferIncentive;
};

export type TermsAppliedIncentives = {
    conditionals?: string[]; // TODO: This should really have been called conditionalIds to make it less confusing
    nonConditionals?: string[]; // TODO: same as above
};

// TODO: This should be defined - it was previous an empty interface which is the equivalent of `any` as well
export type TermsDealSummary = any;

export type TestDrive = {
    firstName: string;
    lastName: string;
    day: Date;
    time: string;
    message: string;
    email: string;
    phone: string;
};

export type TotalTaxesAndFees = {
    taxesAndFeesTotal: number;
    totalTax: number;
};

export type ExtendedFeatureToggles = FeatureToggles & {
    useNewTermIdStandard: boolean;
    F325266_Enable_Template_Rate_Upq_Lock?: boolean;
    enableMultiTradeIns?: boolean;
    useFetchBasedSpinnerMV?: boolean;
    clearManualCreditScoreOnTierSelect?: boolean;
    enableAutoSelectProgramAdminSetting?: boolean;
    overrideTaxFixes_US1984109?: boolean;
    enableCashDownPaymentField?: boolean;
    enableEquityAllocationOrder?: boolean;
    FEAT278181_MV_Quick_Quote_Mode?: boolean;
    F298789_ASP_PG_redesign?: boolean;
    F324220_Enable_Payment_Grid_Persistence?: boolean;
    enableExplicitLenderP1?: boolean;
    enableLeaseResidualTreatment_F286227?: boolean;
    DE783996_Enable_Roll_To_UPQ_On_First_Calculation?: boolean;
    DE784015_Enable_Security_Deposit_Override_Reset?: boolean;
    DE783159_Enable_Negative_Lease_Quote_Rejection?: boolean;
    enableUpqRetry_F283308?: boolean;
    US2127588_Enable_Per_Term_Conditional_Incentive_Narrowing?: boolean;
    DE773860_Enable_Buy_Rate_PQ_First?: boolean;
    DE782162_Send_Vehicle_Fields_On_Payment?: boolean;
    US2131266_Enable_Percent_Tax_Rates?: boolean;
    US2133348_Enable_Per_Term_Incentive_Overrides?: boolean;
    DE773956_Enable_Auto_Select_Guard_Extension?: boolean;
    US2139463_PG_Override_Indicator?: boolean;
    US2161518_Bypass_Dealer_Auto_Select_Setting?: boolean;
    DE773489_Enable_Taxes_Without_Shopper_Zip?: boolean;
    DE771060_Enable_Equity_Allocation_Backfill_Recalc?: boolean;
    DE770955_Enable_Negative_Trade_In_Fix?: boolean;
    DE780047_Enable_One_Pay_Keep_Cash_Down?: boolean;
    DE782944_Enable_Grid_Selected_Term_Tax_Sync?: boolean;
    F311786_SV_Retain_Program?: boolean;
    F320813_Enable_Lease_Due_At_Signing_Section?: boolean;
    DE783787_Enable_UPQ_Residual_Keep_On_Mileage_Change?: boolean;
};

export type PartialInitOfferPayload = {
    coBuyer?: CoBuyer;
    dealExchangeDealId: string;
    dealExchangeVersionId: string;
    featureToggles: ExtendedFeatureToggles;
    incompleteDealDetails?: IncompleteDealDetails;
    sessionId?: string;
    shopper: IShopper;
    vehicle: Vehicle;
};

export type InitOfferPayload = EncryptionFields & {
    cashApplyMethod?: string;
    coBuyer?: CoBuyer;
    creditApp?: CreditApp;
    creditScore: CreditScore;
    creditScoreSource?: CreditScoreSource;
    creditTiers: CreditTiers;
    dealExchangeDealId: string;
    dealExchangeVersionId: string;
    featureToggles: ExtendedFeatureToggles;
    isTargetOutOfPocket?: boolean;
    incentives?: OfferIncentives;
    incompleteDealDetails?: IncompleteDealDetails;
    leaseAnnualMiles?: number[];
    message?: string;
    monthlyPayment: number;
    monthlyPaymentWithoutProducts?: number;
    offer?: any; // TODO: Check - check whether this is used by both DealStarter and ShopperPlatform and what it should contain
    payment?: NonNullable<unknown>;
    penciledInputs: IPenciledInputs;
    sessionId?: string;
    settings?: OfferSettings;
    shopper: IShopper;
    source?: DealerSource;
    terms?: Terms;
    toggles: FeatureToggles;
    totalFinanced: number;
    vehicle: Vehicle;
};

export type SpInitOfferPayload = {
    offer: SpOffer;
    dealer: Dealer;
    vehicle: Vehicle;
};

export type AIOIncentiveItem = {
    incentiveName: string;
    incentiveCode: number;
    incentiveDescription: string;
    incentiveAmount: number;
    incentiveProgramNumber: string;
    incentiveIsStandard?: boolean;
    incentiveIsManual?: boolean;
    incentiveType?: string;
};

export type AIOIncentives = AIOIncentiveItem[];

export type IncompleteDealDetails = {
    missingPaymentData?: boolean;
};

export type GetPaymentDoneCB = (err: string | null, result: any) => void;

export type PencilInputsByOfferType = {
    [offerType in PenciledInputOfferType]: IPencilOfferInputs;
};

export type ProgramPayload = {
    termId?: string;
    selectedIncentives: string[];
};

export type PendingLenderSync = { pendingLenderProgramSync?: boolean };

export type AggregatedIncentives = { allIds: number[]; allNames: string[]; conditionals: string[]; nonConditionals: string[] };

export interface PaymentPayloadCalculationSettings {
    includeDefaultDealerFees?: boolean;
    includeDefaultGovFees?: boolean;
    includeDefaultDealerBasedTaxes: boolean;
    includeDefaultLenderFees?: boolean;
    includeDefaultTaxes?: boolean;
    includeCalculatedGovFees?: boolean;
}

export interface PaymentDealerAddress {
    street: string;
    city: string;
    state: string;
    zip: string;
}

export type GetCashOfferAdditionalSelectors = {
    getTotalVppSelectionPrice: (state: any, offerType?: any) => number;
    getTotalAccessoriesSelectionPrice: (state: any) => number;
    getIsTradeIncluded: (state: any) => boolean;
    getIncludedTradeInsNetValue?: (state: any) => number;
};

export type OfferTermInputs = IPencilOfferInputs | PaymentTerm;

export type NonCashOfferType = typeof FINANCE | typeof LEASE | typeof BALLOON;

export type RateKey = 'sellRate' | 'buyRate';

export type ResidualValues = {
    value?: number;
    months: number;
    unitType: ResidualValueType;
};

export type AppConfigDataOnlyToggles = {
    passCustomerVersionedAddressToPS?: boolean;
    enableEquityAllocation?: boolean;
    enableEquityAllocationOrder?: boolean;
    enableCustomRateCard?: boolean;
    enableDealerRateCardSupport?: boolean;
    enableProgramIncentivesRestoration_F292564?: boolean;
    DE765882_Enable_Applied_Incentive_Display_Fixes?: boolean;
    DE778704_Enable_Response_Applied_Incentive_Amount?: boolean;
    DE779915_Enable_Roll_To_Apply_Incentive_Catalog?: boolean;
    DE780005_Enable_Vehicle_Switch_Standards_Handoff?: boolean;
    DE783865_Enable_Cash_Switch_Standard_Rebate?: boolean;
    DE784135_Enable_Trade_Edit_After_Roll_To?: boolean;
    DE778958_Enable_Keep_Lender_On_Zip_Change?: boolean;
    DE777671_Enable_Edited_Program_Incentive_Dedupe?: boolean;
    DE771128_Enable_UPQ_Rebate_Ledger?: boolean;
    DE771157_Enable_Unpriced_Term_Standards_Carry?: boolean;
    DE773201_Enable_Manual_Rebate_Preservation?: boolean;
    DE773203_Enable_Grid_Cash_Down_Catalog_Carry?: boolean;
    DE773480_Bypass_Program_Loss_Guard_On_Upq?: boolean;
    DE768913_Skip_Null_Cash_Incentive_Ids?: boolean;
    DE774239_Enable_Grid_Manual_Rebate_Main_Row_First?: boolean;
    F319474_Enable_Grid_Incentive_Management?: boolean;
    DE774439_Enable_Conditional_Rebate_Keep_Grid_Open?: boolean;
    DE773854_Stable_Lender_Fees_On_Incentive_Reapply?: boolean;
    DE773664_Honor_Dealer_Standard_Exclusion?: boolean;
    DE773365_Enable_UPQ_All_In_Price_Incentives?: boolean;
    DE766960_Enable_Implausible_Saved_Rate_Clean_Up?: boolean;
    DE778947_Keep_Cash_Incentive_Catalog_Detail?: boolean;
    DE782844_Enable_Cash_Incentive_Name_On_Catalog_Load?: boolean;
    DE783585_Enable_Cash_Manual_Rebate_Provenance?: boolean;
    DE783843_Enable_Cash_Rebate_Ledger_Seed?: boolean;
    DE783842_Enable_Cash_Offer_Price_Fallback?: boolean;
    DE785177_Enable_Grid_Rebate_Ids_Consistency?: boolean;
    F325266_Enable_Template_Rate_Upq_Lock?: boolean;
};

export type FeatureTogglesAndUrlOnly = FeatureToggles & AppConfigDataOnlyToggles;

export type ResetOptions = {
    retainRatesOverride?: boolean;
    previousTermMonth?: number;
    retainSellRateOverride?: boolean;
    retainAcqFeeOverride?: boolean;
    retainSecurityDepositOverride?: boolean;
};

export type AutoTermIdOptions = {
    getPairs?: boolean;
    offerType?: OfferType;
    extension?: ExtensionType;
    paymentTerm?: PaymentTerm;
    pencilInputs?: IPencilOfferInputs;
};

export type AutoTermIdParams = {
    months: number;
    downpayment: number;
    fordflexbuy: number | undefined;
};

export type GetAutoTermId = (params: AutoTermIdParams, getPairs: boolean) => string;

export type GetDownPaymentField = (term: ITerm) => string;

export type DownPaymentInputs = 'dueAtSigning' | 'requestedDownPayment' | 'outOfPocket';

export type DownPaymentOptions = { useResolutionLogic?: boolean };

export type SellRatesFromTerms = (SellRateItem & { percent?: number })[];

/**
 * Payment-grid residual cell, mirroring `SellRateItem`. `val` is null for a term column with no residual.
 *
 * TEMPORARY LOCAL COPY. The canonical home is `ResidualItem` in @makemydeal/dr-platform-types
 * (`shared/types/paymentGrid`), next to `SellRateItem` — added there but not yet released. Once this repo
 * picks up the platform-types version containing it, delete this and import it from there instead, the way
 * `SellRatesFromTerms` below already imports `SellRateItem`.
 */
export type ResidualItem = {
    type?: ResidualValueType;
    val: number | null;
    month: number | null;
};

export type ResidualsFromTerms = ResidualItem[];

export type AdjustedResidualItem = {
    month: number | null;
    amount: number | null;
};

export type TermIncentiveToTransform = {
    incentiveId: number;
    cash: number;
    termIncentives: {
        incentiveTermRange: {
            customerCash?: number;
            apr?: number;
            startTerm: number;
            endTerm: number;
        }[];
    };
};

export type PaymentGridMiddlewareAction = {
    meta?: any;
    payload?: any;
    type: string;
};

export type PaymentGridMiddlewareStore = {
    getState: () => any;
    dispatch: (action: PaymentGridMiddlewareAction) => void;
};

export type PaymentGridMiddlewareNext = (action: PaymentGridMiddlewareAction) => void;

export type PaymentGridMiddleware = (
    middlewareConfig: IPaymentConfig
) => (store: PaymentGridMiddlewareStore) => (next: PaymentGridMiddlewareNext) => (action: PaymentGridMiddlewareAction) => void;

/** Re-exported so consumers keep one import for the prior balance vocabulary. */
export type PriorLeaseBalanceApplyMethod = PlatformPriorLeaseBalanceApplyMethod;
export type PriorLeaseBalanceMethod = paymentServicesTypes.PriorLeaseBalanceMethod;

/**
 * The prior lease balance as Payment Services reports it on a priced offer type. `PriorLeaseBalanceMethod`
 * is the Orchestrator's translation of the method we sent (US2065394): Capitalized -> Capitalize,
 * Upfront -> Upfront.
 */
export type PriorLeaseBalanceFromPayment = {
    priorLeaseBalance?: number;
    priorLeaseBalanceMethod?: PriorLeaseBalanceMethod;
};

/** The request node plus the descriptive fields Manager View keeps on the deal but does not send. */
export type PriorLeaseBalance = PlatformPriorLeaseBalance & {
    payableTo?: string;
    vin?: string;
    lenderOwed?: string;
};

export type PriorLeaseBalanceForPayment = {
    amount: number;
    applyMethod?: PriorLeaseBalanceApplyMethod;
};

export type PenciledInputsState = IPenciledInputs & {
    previousFinanceInputWithoutFlexBuy?: IPencilOfferInputs;
    priorLeaseBalance?: PriorLeaseBalance;
};

export type PartialPenciledInputsWithAllTypes = Partial<PenciledInputsState> & Required<Pick<PenciledInputsState, 'allTypes'>>;

export type AddFlexBuyPaymentGridMetaOptions = Partial<Omit<PaymentGridRequestOptions, 'paymentRequestOverride'>> & {
    isFlexBuyPayment?: boolean;
    gridAction?: boolean;
    paymentRequestOverride?: {
        finance?: {
            termList: number[];
            flexBuyDownPayments: number[];
            flexBuyPercents: number[];
            flexBuyTerms: number[];
        };
    };
};

export type LeaseInputUpdateOptions = {
    term: string;
    offerType: OfferType;
    oldValue?: number;
    newValue?: number;
    isManagerView?: boolean;
};

export type LeaseInputUpdateValues = {
    term?: string;
    downPayment: number;
};

export type IncentiveToLookup = {
    [x: string]: OfferIncentive;
};
